import {ArrayElement, tuple} from "~/types";
import {
    AdmissionInput,
    AdmissionTypeEnum,
    CreateAdmissionDocument,
    CreateAdmissionMutationResult as CreateAdmissionResult,
    CreateAdmissionMutationVariables as CreateAdmissionVars,
    CreateCarAdmissionDocument,
    CreateCarAdmissionMutationResult,
    CreateCarAdmissionMutationVariables, CreatePedestrianAdmissionDocument,
    CreatePedestrianAdmissionMutationResult,
    CreatePedestrianAdmissionMutationVariables,
    GetAdmissionCarListDocument,
    GetAdmissionCarListQueryResult as GetAdmissionCarListResult,
    GetAdmissionCarListQueryVariables as GetAdmissionCarListVars,
    GetAdmissionCompanyDocument as GetAdmissionCompanyDocument,
    GetAdmissionCompanyQueryResult as GetAdmissionCompanyResult,
    GetAdmissionCompanyQueryVariables as GetAdmissionCompanyVars,
    GetAdmissionEmployeeListDocument,
    GetAdmissionEmployeeListQueryResult as GetAdmissionEmployeeListResult,
    GetAdmissionEmployeeListQueryVariables as GetAdmissionEmployeeListVars,
    GetAdmissionListDocument,
    GetAdmissionListQueryResult as GetAdmissionListResult,
    GetAdmissionListQueryVariables as GetAdmissionListVars,
} from "~/queries";
import {useMemo} from "react";
import {useApolloClient, useMutation} from "@apollo/client";
import {useErrorHandler} from "~/components/ErrorHandling";
import { useEffect } from "react";
import {useQuery} from "~/apollo/patches";


export const useAdmission = (id: string) => {
    const handleError = useErrorHandler();
    const {data, loading, error, refetch} = useQuery<GetAdmissionListResult, GetAdmissionListVars>(GetAdmissionListDocument, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-and-network"
    });

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(refetch, 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error]);

    return {
        data: data?.admissions?.find(x => x.id == id),
        loading,
        error
    }
}


export type useAdmissionListOptions = {
    type?: "pedestrian" | "car"
    query?: string,
    statuses?: number[]
}
export const useAdmissionList = (options?: useAdmissionListOptions) => {
    const {...filter} = options;
    const apollo = useApolloClient();
    const handleError = useErrorHandler();
    const {data, loading, error, refetch} = useQuery<GetAdmissionListResult, GetAdmissionListVars>(GetAdmissionListDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            query: filter.query,
            isAuto: {"car": true, "pedestrian": false}[filter.type],
            statuses: filter.statuses,
        }
    });

    const _data = useMemo(() => {
        return data?.admissions;
    }, [data, filter]);

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutID = setTimeout(refetch, 5000);
            return () => clearTimeout(timeoutID);
        }
    }, [error])

    // remove previous cache
    useEffect(() => {
        if (!data && loading) {
            apollo.cache.evict({id: "ROOT_QUERY", fieldName: "admissions"});
        }
    }, [data, loading, apollo])


    return {
        data: _data,
        loading,
        error
    };
}

export const useCreateAdmission = () => {
    const apollo = useApolloClient();
    const handleError = useErrorHandler();

    const onCompleted = () => {
        apollo.cache.evict({id: "ROOT_QUERY", fieldName: "admissions"});
        apollo.cache.evict({id: "ROOT_QUERY", fieldName: "admissionEmployers"});
        apollo.cache.evict({id: "ROOT_QUERY", fieldName: "admissionCars"});
        apollo.cache.evict({id: "ROOT_QUERY", fieldName: "admissionCompany"});
    }

    const [mutationAll, {error: errorAll, data: dataAll, called: calledAll, loading: loadingAll}] = useMutation<CreateAdmissionResult, CreateAdmissionVars>(CreateAdmissionDocument, {
        notifyOnNetworkStatusChange: true,
        onCompleted: onCompleted,
    });

    const [mutationCar, {error: errorCar, data: dataCar, called: calledCar, loading: loadingCar}] = useMutation<CreateCarAdmissionMutationResult, CreateCarAdmissionMutationVariables>(CreateCarAdmissionDocument, {
        notifyOnNetworkStatusChange: true,
        onCompleted: onCompleted,
    });

    const [mutationPedestrian, {error: errorPedestrian, data: dataPedestrian, called: calledPedestrian, loading: loadingPedestrian}] = useMutation<CreatePedestrianAdmissionMutationResult, CreatePedestrianAdmissionMutationVariables>(CreatePedestrianAdmissionDocument, {
        notifyOnNetworkStatusChange: true,
        onCompleted: onCompleted,
    });

    const _mutation = (admission: AdmissionInput, types?: AdmissionTypeEnum[]) => {
        if (types?.includes(AdmissionTypeEnum.Car) && types?.includes(AdmissionTypeEnum.Pedestrian)) {
            return mutationAll({
                variables: {
                    admissionPedestrian: admission,
                    admissionCar: {
                        ...admission,
                        accessZone3: false,
                    }
                }
            })
        }
        if (types?.includes(AdmissionTypeEnum.Car)) {
            return mutationCar({
                variables: {
                    admission: {
                        ...admission,
                        accessZone3: false,
                    },
                }
            })
        }
        if (types?.includes(AdmissionTypeEnum.Pedestrian)) {
            return mutationPedestrian({
                variables: {
                    admission: admission
                }
            })
        }

    }

    // invalidate cache when unmount
    useEffect(() => () => {
        apollo.cache.evict({id: "ROOT_QUERY", fieldName: "admissionEmployers"});
        apollo.cache.evict({id: "ROOT_QUERY", fieldName: "admissionCars"});
        apollo.cache.evict({id: "ROOT_QUERY", fieldName: "admissionCompany"});
    }, [])

    useEffect(() => {
        const error = errorAll || errorCar || errorPedestrian;
        if (error) {
            handleError(error);
        }
    }, [errorAll, errorCar, errorPedestrian])

    return tuple(_mutation, {
        error: errorAll || errorCar || errorPedestrian,
        called: calledAll || calledPedestrian || calledCar,
        loading: loadingAll || loadingCar || loadingPedestrian,
        data: dataAll || dataCar || dataPedestrian
    });
}


export const useAdmissionCompany = () => {
    const handleError = useErrorHandler()
    const {data, loading, error} = useQuery<GetAdmissionCompanyResult, GetAdmissionCompanyVars>(GetAdmissionCompanyDocument, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-and-network",
    });

    useEffect(() => {
        if (error) {
            handleError(error);
        }
    }, [error])

    return {
        data: data?.admissionCompany,
        loading,
    }
}

type useAdmissionEmployeeListOptions = { query?: string }
export const useAdmissionEmployeeList = (options: useAdmissionEmployeeListOptions = {}) => {
    const {query} = options;
    const handleError = useErrorHandler()
    const {data, loading, error} = useQuery<GetAdmissionEmployeeListResult, GetAdmissionEmployeeListVars>(GetAdmissionEmployeeListDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            query
        },
        fetchPolicy: "cache-and-network",
    });

    useEffect(() => {
        if (error) {
            handleError(error);
        }
    }, [error])

    return {
        data: data?.admissionEmployers,
        loading,
    }
}


type useAdmissionCarListOptions = { query?: string }
export const useAdmissionCarList = (options: useAdmissionCarListOptions = {}) => {
    const {query} = options;
    const handleError = useErrorHandler()
    const {data, loading, error} = useQuery<GetAdmissionCarListResult, GetAdmissionCarListVars>(GetAdmissionCarListDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            query
        },
        fetchPolicy: "cache-and-network",
    });

    useEffect(() => {
        if (error) {
            handleError(error);
        }
    }, [error])

    return {
        data: data?.admissionCars,
        loading,
    }
}


export const useGetAdmissionEmployer = () => {
    const apollo = useApolloClient();

    return async (query: string): Promise<ArrayElement<GetAdmissionEmployeeListResult["admissionEmployers"]> | null> => {
        const result = await apollo.query<GetAdmissionEmployeeListResult, GetAdmissionEmployeeListVars>({
            query: GetAdmissionEmployeeListDocument,
            variables: {
                query
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: "network-only",
        });
        return result.data.admissionEmployers[0] || null;
    }
}


export const useGetAdmissionCar = () => {
    const apollo = useApolloClient();

    return async (query: string): Promise<ArrayElement<GetAdmissionCarListResult["admissionCars"]> | null> => {
        const result = await apollo.query<GetAdmissionCarListResult, GetAdmissionCarListVars>({
            query: GetAdmissionCarListDocument,
            variables: {
                query
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: "network-only",
        });
        return result.data.admissionCars[0] || null;
    }
}
